import React from 'react';
import classnames from 'classnames';
import { dfs, dateTypes } from '../../utils';
import { Tooltip } from '@platform/base';

const FLAG_SORT_ASC = 1;
const FLAG_SORT_DESC = -1;
const DEFAULT_SORTER = (col, a, b) => {
    const aValue = a.values[col.key];
    const bValue = b.values[col.key];
    if (col.itemtype === 'number') {
        return (aValue?.value ?? '') - (bValue?.value ?? '');
    } else if(dateTypes.has(col.itemtype)) {
        let aDateString = aValue?.value || '',
            bDateString = bValue?.value || '';
        return aDateString.localeCompare(bDateString);
    } else if(col.itemtype === 'attachment') {
        let aNum = aValue?.value?.split(',')[0] || '',
            bNum = bValue?.value?.split(',')[0] || '';
        return aNum - bNum;
    } else {
        let aDisplay = aValue?._display_ ?? '';
        let bDisplay = bValue?._display_ ?? '';
        if (aDisplay > bDisplay) {
            return 1;
        } else if (aDisplay < bDisplay) {
            return -1;
        } else {
            return 0;
        }
    }
};

export default function useSort(Table){
    return class SortTable extends React.Component {
        render(){
            let { sortConfig = {}, columns = [], locale } = this.props,
                { enable, orders = new Map() } = sortConfig;

            if(enable){
                columns = dfs(columns, col => {
                    if(col.children || !col.sortable){ 
                        return col;
                    }

                    return {
                        ...col,
                        title: (
                            <>
                                {col.title}
                                <Tooltip
                                    placement="top"
                                    overlay={locale['table0041']}
                                    delayShow={300}
                                >
                                    <i className={classnames("header-icon sort-icon", { 'fixed-icon': orders.get(col.key) })}>
                                        <i 
                                            className="icon-container top-icon"
                                            onClick={() => this.handleSortIconClick(col, FLAG_SORT_ASC)}
                                        >
                                            <i 
                                                className={classnames(
                                                    "iconfont icon-shangjiantou",
                                                    { active: orders.get(col.key) === FLAG_SORT_ASC },
                                                )} 
                                            />
                                        </i>
                                        <i 
                                            className="icon-container bottom-icon"
                                            onClick={() => this.handleSortIconClick(col, FLAG_SORT_DESC)}
                                        >
                                            <i 
                                                className={classnames(
                                                    "iconfont icon-xiajiantou",
                                                    { active: orders.get(col.key) === FLAG_SORT_DESC },
                                                )} 
                                            />
                                        </i>
                                    </i>
                                </Tooltip>
                            </>
                        ),
                        sorter: col.sorter || DEFAULT_SORTER.bind(null, col),
                    };
                });
            }

            return (
                enable 
                    ? 
                    <Table 
                        {...this.props} 
                        columns={columns} 
                    /> 
                    : 
                    <Table {...this.props} />
            );
        }

        handleSortIconClick = (col, order) => {
            let { sortConfig = {} } = this.props,
                { orders = new Map(), onSort } = sortConfig,
                colKey = col.key;

            if(orders.get(colKey) === order){
                return; // 屏蔽再次点击取消排序的功能

                // 取消已有排序状态
                // orders.delete(colKey);
            } else {
                // 新增排序状态  TODO 以后改成可配的，现在只支持一个排序
                orders.clear();
                orders.set(colKey, order);
            }

            onSort?.(orders);
        }
    };
}

useSort.displayName = 'useSort';
useSort.dependence = [];
useSort.hooks = function(){
    this._sort = rowKeys => {
        let orders = this.store.sortInfo,
            visibleRowKeysSet = new Set(rowKeys);
        if(!orders?.size) return rowKeys;
        
        rowKeys = [...rowKeys];
        [...orders].forEach(([colKey, order]) => {
            let col = this.getColumnProps(colKey);
            let sorter = col.sorter || DEFAULT_SORTER.bind(null, col);

            rowKeys.sort((rowAKey, rowBKey) => {
                let rowAData = this.getRowData(rowAKey),
                    rowBData = this.getRowData(rowBKey);
                
                return order === FLAG_SORT_ASC ? sorter(rowAData, rowBData) : sorter(rowBData, rowAData);
            });

            this.store.rowKeys.sort((rowAKey, rowBKey) => {
                let rowAData = this.getRowData(rowAKey),
                    rowBData = this.getRowData(rowBKey);
                
                if(!visibleRowKeysSet.has(rowAKey) || !visibleRowKeysSet.has(rowBKey)) return 0;
                
                return order === FLAG_SORT_ASC ? sorter(rowAData, rowBData) : sorter(rowBData, rowAData);
            });
        });

        this._updateDataIndex();
        return rowKeys;
    };
    return {
        getSortInfo: () => {
            return this.store.sortInfo || new Map();
        },
        setSortInfo: orders => {
            this.store.sortInfo = orders || new Map();
            this._updateVisibleRows();
            this.forceUpdate();
        },
    };
};
useSort.propsGenerator = function(props){
    if(!props.sortConfig?.enable){
        return props;
    }

    let { store, sortConfig } = props;

    return {
        ...props,
        sortConfig: {
            ...sortConfig,
            orders: store.getSortInfo(),
            onSort: orders => {
                store.setSortInfo(orders);
                sortConfig?.onSort?.(orders);
            },
        },
    };
};
